/**
 * Proposals API Client
 *
 * HTTP REST API endpoints for Proposals functionality:
 * - Submit proposal for an order (worker only)
 * - Get my proposals (worker)
 * - Get proposals for my order (client)
 *
 * Uses HTTP-only cookies for authentication (via fetchWithTokenRefresh)
 */

import { buildApiUrl } from './config';
import { fetchWithTokenRefresh } from './refreshToken';
import { handleApiError } from './errorHandler';

// Proposal status matching backend
export type ProposalStatus = 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'WITHDRAWN';

export interface CreateProposalRequest {
  proposedPrice: number;
  message: string;
  estimatedDays?: number | null;
}

export interface ProposalResponse {
  id: string;
  orderId: string;
  workerActorId: string;
  proposedPrice: number;
  message: string;
  estimatedDays: number | null;
  status: ProposalStatus;
  createdAt: string;
  updatedAt: string;
  workerActor?: {
    id: string;
    name: string;
    displayName?: string;
    avatarUrl?: string;
  };
  order?: {
    id: string;
    title: string;
    clientActorId: string;
  };
}

export interface UpdateProposalRequest {
  proposedPrice?: number;
  message?: string;
  estimatedDays?: number | null;
}

/**
 * Proposals API
 */
export const proposalsApi = {
  /**
   * Submit proposal for an order
   * POST /orders/:orderId/proposals
   * Requires WORKER role
   */
  async create(orderId: string, data: CreateProposalRequest): Promise<ProposalResponse> {
    const url = buildApiUrl(`/orders/${orderId}/proposals`);
    const response = await fetchWithTokenRefresh(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      // Handle specific error codes
      if (response.status === 409) {
        throw new Error('ALREADY_SUBMITTED');
      }

      if (response.status === 403) {
        throw new Error('WORKER_REQUIRED');
      }

      if (response.status === 400) {
        const message = errorData.message || 'Invalid proposal data';
        throw new Error(message);
      }

      // Extract message from various error formats
      const message =
        errorData.message ||
        (typeof errorData === 'string' ? errorData : null) ||
        'Failed to submit proposal';

      throw new Error(message);
    }

    return response.json();
  },

  /**
   * Get my proposals (as worker)
   * GET /proposals/my-proposals
   */
  async getMyProposals(): Promise<ProposalResponse[]> {
    const url = buildApiUrl('/proposals/my-proposals');
    const response = await fetchWithTokenRefresh(url);

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to fetch proposals');
    }

    return response.json();
  },

  /**
   * Get proposals for an order (as client/order owner)
   * GET /orders/:orderId/proposals
   */
  async getOrderProposals(orderId: string): Promise<ProposalResponse[]> {
    const url = buildApiUrl(`/orders/${orderId}/proposals`);
    const response = await fetchWithTokenRefresh(url);

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to fetch proposals');
    }

    return response.json();
  },

  /**
   * Accept proposal (as client)
   * POST /proposals/:id/accept
   */
  async accept(proposalId: string): Promise<{ message: string; proposal: ProposalResponse }> {
    const url = buildApiUrl(`/proposals/${proposalId}/accept`);
    const response = await fetchWithTokenRefresh(url, {
      method: 'POST',
    });

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to accept proposal');
    }

    return response.json();
  },

  /**
   * Reject proposal (as client)
   * POST /proposals/:id/reject
   */
  async reject(proposalId: string): Promise<{ message: string; proposal: ProposalResponse }> {
    const url = buildApiUrl(`/proposals/${proposalId}/reject`);
    const response = await fetchWithTokenRefresh(url, {
      method: 'POST',
    });

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to reject proposal');
    }

    return response.json();
  },

  /**
   * Update proposal (as worker, pending only)
   * PATCH /proposals/:id
   */
  async update(proposalId: string, data: UpdateProposalRequest): Promise<ProposalResponse> {
    const url = buildApiUrl(`/proposals/${proposalId}`);
    const response = await fetchWithTokenRefresh(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to update proposal');
    }

    return response.json();
  },
};
